import { Injectable, OnDestroy } from '@angular/core';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { Observable, of, ReplaySubject, shareReplay, switchMap, take } from 'rxjs';
import { SettingsService } from '../settings/settings.service';
import { ConfigService } from '../config/config.service';


@Injectable({
  providedIn: 'root'
})
export class FeatureToggleLaunchDarklyService implements OnDestroy {
  private client?: LDClient.LDClient;
  private clientInitialized$: ReplaySubject<void>;

  constructor(
    private settingsService: SettingsService,
    private configService: ConfigService
  ) {
    this.clientInitialized$ = new ReplaySubject<void>(1);

    this.configService.configInitialized.subscribe(() => {
      const launchDarklyConfig = this.configService.launchDarkly;

      const userContext: LDClient.LDSingleKindContext = {
        kind: 'user',
        key: launchDarklyConfig.user.key,
        secondary: launchDarklyConfig.user.secondaryKey,
        ip: launchDarklyConfig.user.ip,
        email: launchDarklyConfig.user.email,
        name: launchDarklyConfig.user.name,
        avatar: launchDarklyConfig.user.avatar,
        firstName: launchDarklyConfig.user.firstName,
        lastName: launchDarklyConfig.user.lastName,
        country: launchDarklyConfig.user.country,
      };

      this.settingsService.SettingsInitialized.subscribe(() => {
        const tenantContext: LDClient.LDSingleKindContext = {
          kind: 'tenant',
          key: this.settingsService.arNumber
        };

        const multiContext: LDClient.LDContext = {
          kind: 'multi',
          user: userContext,
          tenant: tenantContext
        };

        this.client = LDClient.initialize(launchDarklyConfig.clientSideId, multiContext);
        this.clientInitialized$.next();
      });
    });
  }

  public subscribeToFlag(key: string, defaultValue: boolean): Observable<boolean> {
    return this.clientInitialized$.pipe(
      take(1),
      switchMap(() => {
        const toggle$ = new ReplaySubject<boolean>(1);
        this.client
          .waitForInitialization(5)
          .then(() => {
            const currentValue = this.client.variation(key, defaultValue);
            if (typeof currentValue === 'boolean') {
              toggle$.next(currentValue);
              console.log('Feature Toggle:', key, currentValue);
            } else {
              toggle$.next(defaultValue);
              console.error(
                'Unexpected value received from LaunchDarkly. Using default value',
                key,
                currentValue,
                defaultValue
              );
              console.log('Feature Toggle:', key, defaultValue);
            }
            this.client.on(`change:${key}`, flagValue => {
              toggle$.next(typeof flagValue === 'boolean' ? flagValue : defaultValue);
            });
          })
          .catch((error: any) => {
            toggle$.next(defaultValue);
            console.error('Feature toggle lookup failed. Using default value:', key, error, defaultValue);
          });
        return toggle$.asObservable().pipe(shareReplay(1));
      })
    );
  }

  public async ngOnDestroy() {
    if (this.client) {
      await this.client.close();
    }
  }
}
